import { Box, Typography } from "../legacy-ui/materialCompat";
import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import {
  getAgeRangePublicRoute,
  isContentItemAllowedForAgeRange,
} from "../../utils/ageRange.util";
import ViewMoreCard from "../common/ViewMoreCard";
import { useInputMode } from "../../utils/hooks/useInputMode";
import OptimizedImage from "../common/OptimizedImage";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

const MovieItem = ({ item = {}, onFocus, nextPlayGuide }) => {
  const navigate = useNavigate();
  const { contentPhotos, tags, slug, contentType, _id } = item;
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      const contentKind = contentType?.kind || "movie";
      if (contentKind === "music") {
        navigate(`/${contentKind}/${slug}`, {
          state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
        });
      } else {
        navigate(`/${contentKind}/${slug}`);
      }
    },
    focusKey: "content_" + _id,
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Use optimized image with LQIP and WebP support (w=200, q=90)
  const imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });

  return (
    <Box
      ref={ref}
      onMouseEnter={handleMouseEnter}
      sx={{
        width: "250px",
        minWidth: "215px",
        minHeight: "330px",
        position: "relative",
        marginBottom: "5%",
        border: focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive,
        overflow: "hidden",
        zIndex: focused ? 10 : 0,
        margin: "2px",
        borderRadius: "15px",
      }}
      onClick={() => {
        const contentKind = contentType?.kind || "movie";
        if (contentKind === "music") {
          navigate(`/${contentKind}/${slug}`, {
            state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
          });
        } else {
          navigate(`/${contentKind}/${slug}`);
        }
      }}
      className="cursor-pointer rounded-2xl !mx-7"
    >
      <OptimizedImage
        src={imageData.src}
        webpSrc={imageData.webpSrc}
        lqipSrc={imageData.lqipSrc}
        alt={item.title}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      {tags?.[0] && <ContentTag label={tags?.[0]} />}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          opacity: focused ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 0.2s",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontSize: "20px",
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          {item.title}
        </Typography>
        <Typography
          sx={{
            color: "white",
            fontSize: "16px",
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          {item.year}
        </Typography>
      </Box>
    </Box>
  );
};

export const MoviesSlider = ({
  movies,
  onFocus,
  rowTitle,
  shouldFocusSelf,
  id,
  actionLink = null,
  nextPlayGuide = null,
}) => {
  const ageRange = getAgeRangePublicRoute();
  const _items = movies.filter((x) => isContentItemAllowedForAgeRange(x, ageRange));
  const { ref, focusKey, focusSelf } = useFocusable({ onFocus });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (shouldFocusSelf === true) {
      focusSelf();
    }
  }, [shouldFocusSelf]);

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  // Show View More card when items > 12
  const showViewMore = _items.length > 12;

  const { isMouseFocus } = useInputMode();
  const onAssetFocus = useCallback(
    (props) => {
      // Skip scroll adjustment on mouse focus
      if (isMouseFocus) return;
      const { x } = props;
      if (x < 0) {
        innerRef.current.style.transform = `translateX(${Math.abs(x - 300)}px)`;
      } else {
        innerRef.current.style.transform = `translateX(0px)`;
      }
    },
    [scrollingRef, isMouseFocus]
  );

  if (_items.length < 1) return null;
  return (
    <FocusContext.Provider value={focusKey}>
      <Box ref={ref} className="legacy-row-fade-left">
        <Typography className="legacy-row-title legacy-row-title-right">{rowTitle}</Typography>
        <Box
          ref={scrollingRef}
          className="legacy-row-scroller legacy-row-scroller-padded"
          style={
            selected > 5
              ? {
                paddingRight: 0,
              }
              : {
                paddingRight: 40,
              }
          }
        >
          <Box
            className="legacy-row-grid-content legacy-row-scroller-content-animated grid-cols-5"
            ref={innerRef}
          >
            {_items?.map((item, itemIndex) => {
              return (
                <MovieItem
                  item={item}
                  key={item._id}
                  onFocus={(e) => {
                    setSelected(itemIndex);
                    onAssetFocus(e);
                  }}
                  nextPlayGuide={nextPlayGuide}
                />
              );
            })}
            {/* View More Card - shown at end when items > 12 */}
            {showViewMore && (
              <div className="pt-5">
                <ViewMoreCard
                  actionLink={actionLink || `genre;${id}`}
                  shapeType="movie"
                  focusKey={`view_more_${id}`}
                  onFocus={(e) => {
                    setSelected(_items.length);
                    onAssetFocus(e);
                  }}
                />
              </div>
            )}
          </Box>
        </Box>
      </Box>
    </FocusContext.Provider>
  );
};
